/**
 * CE QUE LA CONSOLE DIT DE LA BASE DE SAVOIR — les mots de la relecture d'une
 * fiche, d'une capsule, d'une erreur et de son remède (chantier « manuel
 * vivant », Φ-MV-2, MV-CNT-05, MV-CNT-06).
 *
 * Fonctions pures, sans React : c'est ici que se décide ce qui s'écrit à
 * l'écran, et c'est ici que cela s'éprouve (`savoir-format.test.ts`).
 *
 * ─── Deux axes, qu'on ne confond jamais ─────────────────────────────────────
 *
 *  - LE SERVICE (`statut`) : seul un contenu « Publié » est servi à un élève.
 *  - LA RELECTURE (`statutRelecture`) : elle progresse PENDANT le service. Un
 *    contenu importé naît publié et « produit par agent — non relu » (AD-MV-8,
 *    AD-MV-19 : « publié, puis relu »). Relire ne publie rien, publier ne relit
 *    rien : la page montre les deux, côte à côte, chacun avec sa pastille.
 *
 * ─── Aucun code interne à l'écran (MV-UX-04) ────────────────────────────────
 *
 * Une notion se nomme par son rang et son titre, une erreur par son intitulé,
 * une fiche et une capsule par leur titre. Les codes stables restent dans les
 * adresses.
 */
import {
  LIBELLE_NON_RELU,
  TRANSITIONS_STATUT_SAVOIR,
  denombrer,
  type AncreFiche,
  type CategorieErreur,
  type ContentStatus,
  type ErreurRelectureDto,
  type NotionDeRessourceDto,
  type SavoirChapitreResponse,
  type SorteSavoir,
  type StatutRelecture,
} from '@silex/shared';

import { rangNotion, statutChapitre } from './programme-format';
import type { BadgeTone } from './suivi-format';
import { formatDate } from './tuteur-format';

/* ─────────────────────────── les notions ─────────────────────────── */

/**
 * Les notions qu'une ressource sert, en clair : « 3.2 Multiplier et diviser
 * des puissances ». Plusieurs notions qui couvrent tout le chapitre : « Toutes
 * les notions » (ressource de révision). Une notion hors du chapitre n'a pas
 * de rang : son titre seul.
 */
export function nommerNotions(
  notions: readonly NotionDeRessourceDto[],
  rangChapitre: number,
  nbNotionsDuChapitre?: number,
): string {
  if (notions.length === 0) return 'Aucune notion rattachée';
  if (
    notions.length > 1 &&
    nbNotionsDuChapitre !== undefined &&
    notions.filter((n) => n.rang !== null).length >= nbNotionsDuChapitre
  ) {
    return 'Toutes les notions';
  }
  return [...notions]
    .sort((a, b) => (a.rang ?? Number.MAX_SAFE_INTEGER) - (b.rang ?? Number.MAX_SAFE_INTEGER))
    .map((n) => (n.rang === null ? n.titre : `${rangNotion(rangChapitre, n.rang)} ${n.titre}`))
    .join(' · ');
}

/* ─────────────────────────── le service ─────────────────────────── */

/** Le statut de service d'une fiche ou d'une capsule — les mots du chapitre. */
export function statutService(statut: ContentStatus): { tone: BadgeTone; label: string } {
  return statutChapitre(statut);
}

/** Une action de statut offerte à l'écran. */
export interface ActionDeStatut {
  readonly vers: ContentStatus;
  readonly libelle: string;
  /** La question posée avant d'agir : ce que le geste change POUR L'ÉLÈVE. */
  readonly confirmation: string;
}

type CibleDeStatut = Exclude<SorteSavoir, 'erreurs'> | 'chapitres';

/** Les accords de chaque cible, écrits une fois — pas devinés à partir d'un article. */
const ACCORDS: Readonly<
  Record<CibleDeStatut, { Le: string; le: string; elle: string; e: string }>
> = {
  fiches: { Le: 'La fiche', le: 'la fiche', elle: 'elle', e: 'e' },
  capsules: { Le: 'La capsule', le: 'la capsule', elle: 'elle', e: 'e' },
  chapitres: { Le: 'Le chapitre', le: 'le chapitre', elle: 'il', e: '' },
};

function actionVers(de: ContentStatus, vers: ContentStatus, cible: CibleDeStatut): ActionDeStatut {
  const { Le, le, elle, e } = ACCORDS[cible];
  switch (vers) {
    case 'en_revue':
      return de === 'publie'
        ? {
            vers,
            libelle: 'Sortir du service pour reprise',
            confirmation: `${Le} ne sera plus servi${e} aux élèves tant qu’${elle} n’aura pas été republié${e}.`,
          }
        : {
            vers,
            libelle: de === 'valide' ? 'Remettre en relecture' : 'Mettre en relecture',
            confirmation: `${Le} repasse en relecture. Rien ne change pour les élèves : ${elle} n’est pas en service.`,
          };
    case 'valide':
      return {
        vers,
        libelle: 'Valider',
        confirmation: `${Le} est jugé${e} juste. Valider ne met pas en service : il restera à publier.`,
      };
    case 'publie':
      return {
        vers,
        libelle: cible === 'chapitres' ? 'Publier le chapitre' : 'Publier',
        confirmation: `${Le} sera servi${e} aux élèves dès maintenant.`,
      };
    case 'retire':
      return {
        vers,
        libelle: 'Retirer définitivement',
        confirmation: `Un retrait ne se défait pas : ${le} ne reviendra en service que par une version neuve.`,
      };
    default:
      return { vers, libelle: statutChapitre(vers).label, confirmation: '' };
  }
}

/**
 * Les changements de statut praticables depuis `statut`, dans l'ordre où la
 * page les offre : d'abord ce qui AVANCE vers le service, le retrait en
 * dernier. La table est celle du contrat (`TRANSITIONS_STATUT_SAVOIR`) : la
 * console n'offre jamais un geste que le serveur refuserait par principe.
 *
 * Un chapitre ne se « retire » pas d'ici (il n'a pas de version qui le
 * remplace) : on le sort du service, on le republie.
 */
export function actionsDeStatut(
  statut: ContentStatus,
  cible: CibleDeStatut,
): readonly ActionDeStatut[] {
  const ordre: readonly ContentStatus[] = ['publie', 'valide', 'en_revue', 'retire'];
  return ordre
    .filter((vers) => TRANSITIONS_STATUT_SAVOIR[statut].includes(vers))
    .filter((vers) => !(cible === 'chapitres' && vers === 'retire'))
    .map((vers) => actionVers(statut, vers, cible));
}

/* ─────────────────────────── la relecture ─────────────────────────── */

/** Où en est la relecture d'un contenu, en mots. */
export interface EtatDeRelecture {
  /** `false` : personne ne l'a relu — il compte dans « non relu ». */
  readonly relu: boolean;
  readonly tone: BadgeTone;
  readonly label: string;
}

const VERBE_RELU: Readonly<Record<SorteSavoir, { relu: string; corrige: string }>> = {
  fiches: { relu: 'Relue', corrige: 'Corrigée' },
  /* Une capsule ne se lit pas : elle s'écoute (MV-CNT-06, « écoutée par »). */
  capsules: { relu: 'Écoutée', corrige: 'Refaite' },
  erreurs: { relu: 'Relue', corrige: 'Corrigée' },
};

/**
 * « Produit par agent — non relu », ou « Relue par Camille Roy le 9 octobre
 * 2026 ». Le nom et la date sont ce qui fait d'une relecture un FAIT : sans
 * eux, « relu » ne serait qu'une case cochée.
 */
export function etatRelecture(
  e: {
    readonly statutRelecture: StatutRelecture;
    readonly relecteur: string | null;
    readonly releLe: string | null;
  },
  sorte: SorteSavoir,
): EtatDeRelecture {
  if (e.statutRelecture === 'genere') {
    return { relu: false, tone: 'a-revoir', label: LIBELLE_NON_RELU };
  }
  const verbe = VERBE_RELU[sorte][e.statutRelecture === 'corrige' ? 'corrige' : 'relu'];
  const par = e.relecteur ? ` par ${e.relecteur}` : '';
  const le = e.releLe ? ` le ${formatDate(e.releLe)}` : '';
  return { relu: true, tone: 'acquis', label: `${verbe}${par}${le}` };
}

/** Le bouton de relecture : on RELIT une fiche, on ÉCOUTE une capsule. */
export function libelleMarquerRelu(sorte: SorteSavoir): string {
  return sorte === 'capsules' ? 'Écoutée, rien à changer' : 'Relu, rien à changer';
}

/* ─────────────────────────── les résumés des sections ─────────────────────────── */

interface MotsDuResume {
  readonly un: string;
  readonly plusieurs: string;
  readonly aucune: string;
  /** « relue » / « relues » — « écoutée » / « écoutées ». */
  readonly relu: readonly [string, string];
}

function resume(total: number, nonRelus: number, mots: MotsDuResume): string {
  if (total === 0) return mots.aucune;
  const compte = denombrer(total, mots.un, mots.plusieurs);
  if (nonRelus === 0) return `${compte} · ${total > 1 ? `toutes ${mots.relu[1]}` : mots.relu[0]}`;
  return `${compte} · ${nonRelus} non ${nonRelus > 1 ? mots.relu[1] : mots.relu[0]}`;
}

/**
 * La ligne visible quand une section du savoir est REPLIÉE : le compte, et ce
 * qui reste à relire (MV-CNT-05 : « la console montre le compte "non relu" du
 * chapitre ; relire une fiche le fait baisser de 1 »).
 */
export function resumesDuSavoir(s: SavoirChapitreResponse): {
  readonly fiches: string;
  readonly capsules: string;
  readonly erreurs: string;
} {
  const sansAncre = s.erreursSansAncre.length;
  const erreurs = resume(s.erreurs.length, s.nonRelus.erreurs, {
    un: 'erreur',
    plusieurs: 'erreurs',
    aucune: 'Aucune erreur',
    relu: ['relue', 'relues'],
  });
  return {
    fiches: resume(s.fiches.length, s.nonRelus.fiches, {
      un: 'fiche',
      plusieurs: 'fiches',
      aucune: 'Aucune fiche',
      relu: ['relue', 'relues'],
    }),
    capsules: resume(s.capsules.length, s.nonRelus.capsules, {
      un: 'capsule',
      plusieurs: 'capsules',
      aucune: 'Aucune capsule',
      relu: ['écoutée', 'écoutées'],
    }),
    erreurs: sansAncre > 0 ? `${erreurs} · ${sansAncre} sans remède en service` : erreurs,
  };
}

/** Le total de ce qui n'a pas été relu, toutes sortes confondues. */
export function totalNonRelus(s: SavoirChapitreResponse): number {
  return s.nonRelus.fiches + s.nonRelus.capsules + s.nonRelus.erreurs;
}

/* ─────────────────────────── les erreurs et leurs remèdes ─────────────────────────── */

const CATEGORIES: Readonly<Record<CategorieErreur, string>> = {
  procedurale: 'Procédure mal exécutée',
  conceptuelle: 'Concept à reprendre',
  lecture_enonce: 'Énoncé mal lu',
  lecture_figure: 'Figure mal lue',
  langue: 'Langue',
  methode: 'Méthode inadaptée',
};

export function libelleCategorie(categorie: CategorieErreur): string {
  return CATEGORIES[categorie];
}

/**
 * Le remède d'une erreur existe-t-il EN SERVICE ? C'est la question de
 * « toute erreur a son ancre » (MV-SAV-07), posée erreur par erreur : un
 * remède pointé vers une fiche retirée n'envoie l'élève nulle part.
 */
export function etatDuRemede(e: Pick<ErreurRelectureDto, 'aSonAncre' | 'remedes'>): {
  readonly tone: BadgeTone;
  readonly label: string;
} {
  if (e.remedes.length === 0) return { tone: 'a-revoir', label: 'Aucun remède' };
  if (!e.aSonAncre) return { tone: 'a-revoir', label: 'Remède hors service' };
  return { tone: 'acquis', label: 'Remède en service' };
}

/** Ce vers quoi le remède envoie, en une ligne : « Fiche « … », au piège « … » · capsule « … » ». */
export function direRemedes(e: Pick<ErreurRelectureDto, 'remedes'>): string {
  if (e.remedes.length === 0) return 'Aucune fiche ni capsule ne répond à cette erreur.';
  return e.remedes
    .map((r) =>
      [
        r.fiche ? `fiche « ${r.fiche.ficheTitre} », au piège « ${r.fiche.titre} »` : null,
        r.capsule ? `capsule « ${r.capsule.titre} »` : null,
      ]
        .filter((m): m is string => m !== null)
        .join(' · '),
    )
    .join(' ; ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Un piège d'une fiche, et l'erreur du catalogue à laquelle il répond. */
export interface PiegeDeLaFiche {
  readonly ancre: string;
  readonly titre: string;
  /** L'erreur, si le catalogue du chapitre la porte ; `null` sinon — un piège orphelin. */
  readonly erreur: Pick<ErreurRelectureDto, 'code' | 'titre' | 'exemple'> | null;
}

/**
 * Pour chaque piège d'une fiche, l'erreur qui lui répond (MV-CNT-06 : « pour
 * chaque erreur du chapitre, le piège qui lui répond »). Un piège dont le code
 * d'erreur est absent du catalogue est rendu avec `erreur: null` : la page le
 * dit, plutôt que de le taire.
 */
export function piegesDeLaFiche(
  ancres: readonly AncreFiche[],
  erreurs: readonly ErreurRelectureDto[],
): readonly PiegeDeLaFiche[] {
  const parCode = new Map(erreurs.map((e) => [e.code, e]));
  return ancres.flatMap((a): PiegeDeLaFiche[] => {
    if (a.erreur === undefined) return [];
    const e = parCode.get(a.erreur);
    return [
      {
        ancre: a.ancre,
        titre: a.titre,
        erreur: e ? { code: e.code, titre: e.titre, exemple: e.exemple } : null,
      },
    ];
  });
}

/* ─────────────────────────── les adresses ─────────────────────────── */

/** Les sous-pages de relecture d'un chapitre : une adresse chacune, qui s'envoie. */
export const adresses = {
  chapitre: (chapitre: string): string => `/programme/${encodeURIComponent(chapitre)}`,
  fiche: (chapitre: string, fiche: string, ancre?: string): string =>
    `/programme/${encodeURIComponent(chapitre)}/fiches/${encodeURIComponent(fiche)}${
      ancre === undefined ? '' : `?ancre=${encodeURIComponent(ancre)}`
    }`,
  capsule: (chapitre: string, capsule: string): string =>
    `/programme/${encodeURIComponent(chapitre)}/capsules/${encodeURIComponent(capsule)}`,
  erreur: (chapitre: string, erreur: string): string =>
    `/programme/${encodeURIComponent(chapitre)}/erreurs/${encodeURIComponent(erreur)}`,
} as const;

/* ─────────────────────── « suivante » dans la relecture (L4) ─────────────────────── */

export interface VoisinsDeRelecture {
  /** 1 pour le premier. */
  readonly position: number;
  readonly total: number;
  readonly precedent: string | null;
  readonly suivant: string | null;
  /** Le prochain contenu NON relu après celui-ci (en bouclant), `null` s'il n'y en a plus. */
  readonly prochainARelire: string | null;
}

/**
 * Où aller depuis un contenu de relecture — refonte de la console, L4. Chaque
 * relecture renvoyait au chapitre, où il fallait redéplier la section : « Relu »
 * mène désormais au prochain contenu NON relu de la même sorte, et la page
 * porte « précédente / suivante ». `null` si le code n'est pas dans la liste.
 */
export function voisinsDeRelecture(
  liste: readonly { readonly code: string; readonly relu: boolean }[],
  code: string,
): VoisinsDeRelecture | null {
  const i = liste.findIndex((x) => x.code === code);
  if (i < 0) return null;
  let prochainARelire: string | null = null;
  for (let k = 1; k < liste.length; k += 1) {
    const x = liste[(i + k) % liste.length]!;
    if (!x.relu) {
      prochainARelire = x.code;
      break;
    }
  }
  return {
    position: i + 1,
    total: liste.length,
    precedent: i > 0 ? liste[i - 1]!.code : null,
    suivant: i < liste.length - 1 ? liste[i + 1]!.code : null,
    prochainARelire,
  };
}
